import { Checkbox } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useState } from 'react';

import { bulkUpdateChannelGroup } from '~/src/dash/api/console-api';
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  spacing,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { extractRouteParams, navigate, RouteChannelsView } from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { routeName } from '~/src/dash/utils/routes';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { BulkUpdateSidebar } from '~/src/dash/views/bulk-update-channel-groups/BulkUpdateChannelGroups/BulkUpdateSidebar';
import { BulkUpdateValues } from '~/src/dash/views/bulk-update-channel-groups/BulkUpdateChannelGroups/BulkUpdateValues';
import {
  buildUpdateMapForAPI,
  bulkUpdateChannelGroupsConfig,
  BulkUpdateChannelGroupValueKeys,
} from '~/src/dash/views/bulk-update-channel-groups/config';
import { BulkUpdateChannelGroupContext } from '~/src/dash/views/bulk-update-channel-groups/contexts';
import {
  generateSnackbarState,
  ISnackbarState,
  SnackbarView,
} from '~/src/dash/views/snackbar-view';

// get config
const cfg = bulkUpdateChannelGroupsConfig;

export const BulkUpdateChannelGroups = () => {
  const { classes, location, session, user } = useBaseStore();
  const { pathname } = location;

  // transform page URL into URLSearchParams object; easier for extracting params
  const queryString = location.search;
  const urlParams = new URLSearchParams(queryString);

  // extract the value of the "bulk_edit_ids" parameter
  const bulkUpdateParameterString = urlParams.get('bulk_edit_ids') as string; // ex: "19291,15209"
  const bulkUpdateChannelGroupIDs = bulkUpdateParameterString
    .split(',')
    .map((idStr: string) => parseInt(idStr, 10)); // ex: [19291, 15209]

  const [snackbar, setSnackbar] = useState<ISnackbarState>(generateSnackbarState());
  const [selectedValuesToUpdate, setSelectedValuesToUpdate] = useState<Record<string, IMenuList>>({
    Channels: {},
  });
  const [activeFields, setActiveFields] = useState<Record<string, boolean>>({});
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);

  const navigateToChannelList = () => {
    navigate(
      RouteChannelsView,
      extractRouteParams({
        session,
        user,
      }),
    );
  };

  const handleSnackbarClose = () => {
    setSnackbar(generateSnackbarState());
  };

  const handleActiveFieldsToUpdate = (field: string) => {
    const clonedFields = cloneDeep(activeFields);
    clonedFields[field] = !clonedFields[field];
    if (clonedFields[field] === false && Object.values(selectedValuesToUpdate[field]).length > 0) {
      const clonedSelectedValuesToUpdate = cloneDeep(selectedValuesToUpdate);
      clonedSelectedValuesToUpdate[field] = {};
      setSelectedValuesToUpdate(clonedSelectedValuesToUpdate);
    }
    setActiveFields(clonedFields);
  };

  const handleIDSelectionAccept = (key: string, list: IMenuList) => {
    const clonedValuesToUpdate = cloneDeep(selectedValuesToUpdate);
    const clonedActiveFields = cloneDeep(activeFields);
    clonedValuesToUpdate[key] = list;
    if (Object.values(clonedValuesToUpdate[key]).length > 0) {
      clonedActiveFields[key] = true;
      setActiveFields(clonedActiveFields);
    }
    setSelectedValuesToUpdate(clonedValuesToUpdate);
  };

  const handleDisablingSaveButton = () => {
    let result = Object.values(activeFields).every((value) => value === false);
    if (Object.values(selectedValuesToUpdate.Channels).length === 0 && activeFields.Channels) {
      result = true;
    }
    return result;
  };

  const onSave = () => {
    const updateMap = buildUpdateMapForAPI(selectedValuesToUpdate);
    bulkUpdateChannelGroup({
      query: {
        publisher_id: session.publisher.ID,
        channel_group_ids: bulkUpdateParameterString,
        updates: updateMap,
      },
    })
      .then((res: any) => {
        if (!res || res?.error) {
          setSnackbar({
            message: 'Request failed',
            error: true,
            warning: false,
          } as ISnackbarState);
          return;
        }
        if (res && !res.SmartFloorsUpdated) {
          setSnackbar({
            message:
              'Successfully updated channel groups but failed to update smart floors associated with them.',
            error: true,
            warning: false,
          } as ISnackbarState);
          return;
        }
        navigateToChannelList();
      })
      .catch((err) => {
        setSnackbar({
          message: err,
          error: true,
          warning: false,
        } as ISnackbarState);
      });
  };

  const renderReviewEditedFieldsForConfirmationModal = () => {
    return (
      <>
        <Text style={{ color: '#666EFA' }}>Review edited fields:</Text>
        {activeFields &&
          Object.entries(activeFields).map(
            ([field, value]: [BulkUpdateChannelGroupValueKeys, boolean]) => {
              const updateValue = selectedValuesToUpdate[field];
              let displayValueString;
              switch (typeof updateValue) {
                case 'object':
                  const fieldValueLength = Object.keys(updateValue).length;
                  const updatedMenuTitle =
                    fieldValueLength > 1
                      ? cfg.valuesToUpdate[field].menuTitle
                      : cfg.valuesToUpdate[field].singularTitle;
                  displayValueString = `${fieldValueLength} ${updatedMenuTitle} applied to Channel Groups`;
                  break;
                default:
                  displayValueString = `${updateValue}`;
              }
              return (
                <>
                  {!!value && (
                    <Row noPadding>
                      <Checkbox
                        onClick={() => handleActiveFieldsToUpdate(field)}
                        checked={!!selectedValuesToUpdate[field]}
                      />
                      {displayValueString}
                    </Row>
                  )}
                </>
              );
            },
          )}
      </>
    );
  };

  return (
    <BulkUpdateChannelGroupContext.Provider value={{ cfg }}>
      <Panels
        maxWidth
        vertical
        relative
        minWidthMain
        style={{
          height: '100%',
        }}
      >
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            title={routeName(pathname)}
            session={session}
            user={user}
            publisher={session.publisher}
            classes={classes}
            currentPath={pathname}
            disableTitlePaddingLeft
          />
        </Panel>
        <Panel
          smallVerticalPadding
          hugeHorizontalPadding
          style={{
            borderBottom: `1px solid ${publicaColors.lightGray}`,
          }}
        >
          <Cols
            style={{
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Col paddingRight={spacing.medium}>
              <ButtonV2
                disabled={handleDisablingSaveButton()}
                onClick={() => setConfirmationModal(true)}
              >
                Save
              </ButtonV2>
            </Col>
            <Col>
              <TextButton
                color={'#707070'}
                onClick={navigateToChannelList}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '16px',
                }}
              >
                Close
              </TextButton>
            </Col>
          </Cols>
        </Panel>
        <Panel scroll highlighted flex paddingLeft='80px'>
          <Cols max style={{ position: 'relative' }}>
            <Col flex paddingTop='40px'>
              <Text subheader style={{ paddingBottom: '24px' }}>
                Channel Group Settings
              </Text>
              <BulkUpdateValues
                user={user}
                session={session}
                handleIDSelectionAccept={handleIDSelectionAccept}
                handleActiveFieldsToUpdate={handleActiveFieldsToUpdate}
                activeFields={activeFields}
              />
            </Col>
            <BulkUpdateSidebar
              channelGroupsLength={bulkUpdateChannelGroupIDs.length}
              selectedValuesToUpdate={selectedValuesToUpdate}
              deselect={handleActiveFieldsToUpdate}
              activeFields={activeFields}
            />
          </Cols>
        </Panel>
        <ConfirmationDialogButton
          title='Confirm Bulk Edit Channel Groups'
          description={`The ${bulkUpdateChannelGroupIDs.length} selected channel groups will be updated with new values as displayed in the summary.`}
          renderReactElement={() => renderReviewEditedFieldsForConfirmationModal()}
          generateAcceptButtonStyles={() => ({
            backgroundColor: getColorTheme('channelBidderPrimaryButton'),
            color: getColorTheme('channelBidderPrimaryButtonText'),
          })}
          acceptButtonTitle='Yes, confirm updates'
          acceptButtonCallBack={onSave}
          refuseButtonTitle='Cancel'
          manualOpen={confirmationModal}
          handleClose={() => setConfirmationModal(false)}
          disableSave={handleDisablingSaveButton()}
        />
        <SnackbarView
          autoHideDuration={12000}
          open={!!snackbar.message}
          error={!!snackbar.error}
          onClose={handleSnackbarClose}
          messageColor={''}
          backgroundColor={''}
          closeColor={''}
          warning={!!snackbar.warning}
        >
          {snackbar.message}
        </SnackbarView>
      </Panels>
    </BulkUpdateChannelGroupContext.Provider>
  );
};
